(() => {

  /* ===============================
     Helpers
  =============================== */

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window?.Shopify?.currency?.active || 'EUR'
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  };

  const escapeHtml = (str) => {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Cache para detalles de variantes
  const variantCache = new Map();

  async function fetchVariant(id) {
    const vid = Number(id);
    if (!vid) throw new Error('Variant inválida');
    if (variantCache.has(vid)) return variantCache.get(vid);

    const res = await fetch(`/variants/${vid}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('No se pudo cargar la variante');
    const data = await res.json();
    variantCache.set(vid, data);
    return data;
  }

  async function enrichVariantsIfNeeded(product, handle) {
    const needs = (product?.variants || []).some(v =>
      typeof v.inventory_quantity === 'undefined' ||
      typeof v.inventory_policy === 'undefined' ||
      typeof v.inventory_management === 'undefined'
    );
    if (!needs) return product;

    const ids = (product.variants || []).map(v => Number(v.id)).filter(Boolean);

    const concurrency = 6;
    let i = 0;
    const workers = Array.from({ length: concurrency }, async () => {
      while (i < ids.length) {
        const id = ids[i++];
        try {
          await fetchVariant(id);
        } catch {}
      }
    });
    await Promise.all(workers);

    product.variants = product.variants.map(v => {
      const cached = variantCache.get(Number(v.id));
      return cached ? { ...v, ...cached } : v;
    });

    const stillMissing = (product.variants || []).some(v => {
      const manages = !!v.inventory_management;
      const canOversell = String(v.inventory_policy || '').toLowerCase() === 'continue';
      if (!manages || canOversell) return false;
      return typeof v.inventory_quantity === 'undefined' || Number.isNaN(Number(v.inventory_quantity));
    });

    if (stillMissing && handle) {
      try {
        const invData = await fetchInventoryView(handle);
        const invMap = new Map((invData?.variants || []).map(x => [Number(x.id), x]));
        if (invMap.size) {
          product.variants = product.variants.map(v => {
            const extra = invMap.get(Number(v.id));
            return extra ? { ...v, ...extra } : v;
          });
        }
      } catch {}
    }

    return product;
  }

  function cartQtyByVariant(cart) {
    const map = {};
    (cart?.items || []).forEach(it => {
      const id = Number(it.variant_id);
      map[id] = (map[id] || 0) + Number(it.quantity || 0);
    });
    return map;
  }

  function isVariantEffectivelyAvailable(v, qtyInCart = 0) {
    if (!v) return false;
    if (v.available === false) return false;

    const managesInventory = !!v.inventory_management;
    const canOversell = String(v.inventory_policy || '').toLowerCase() === 'continue';
    if (!managesInventory || canOversell) return true;

    const inv = Number(v.inventory_quantity ?? NaN);
    if (Number.isNaN(inv)) return true;

    return (inv - Number(qtyInCart || 0)) > 0;
  }

  function applyAvailabilityToSelect(product, sel, qtyMap) {
    if (!sel || !product?.variants) return;

    const byId = new Map(product.variants.map(v => [Number(v.id), v]));

    const options = Array.from(sel.options || []);
    options.forEach(opt => {
      const vid = Number(opt.value);
      const v = byId.get(vid);
      if (!v) return;

      const base = opt.getAttribute('data-base-label') || opt.textContent;
      const inCart = qtyMap[vid] || 0;

      const managesInventory = !!v.inventory_management;
      const canOversell = String(v.inventory_policy || '').toLowerCase() === 'continue';

      let soldOutBecauseInCart = false;

      if (managesInventory && !canOversell) {
        const inv = Number(v.inventory_quantity ?? NaN);
        if (!Number.isNaN(inv)) {
          soldOutBecauseInCart = (inv - inCart) <= 0;
        }
      }

      const ok = isVariantEffectivelyAvailable(v, inCart);

      let label = base;
      if (inCart > 0) {
        label = `${base} — En tu bolsa`;
      } else if (!ok) {
        label = `${base} — Agotado`;
      }

      opt.disabled = !ok;
      opt.textContent = label;
    });
  }

  async function refreshVariantAvailability(product, sel) {
    try {
      const cart = await fetchCart();
      const qtyMap = cartQtyByVariant(cart);
      applyAvailabilityToSelect(product, sel, qtyMap);
      return qtyMap;
    } catch (e) {
      return null;
    }
  }

  async function fetchInventoryView(handle) {
    const res = await fetch(`/products/${handle}?view=lb-inventory`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('No se pudo cargar inventario (view)');
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  async function fetchProduct(handle) {
    const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('No se pudo cargar el producto');
    return res.json();
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('No se pudo leer el carrito');
    return res.json();
  }

  function openDawnCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    const details = document.querySelector('#Details-cart-drawer-container');
    if (details && details.tagName === 'DETAILS') {
      details.setAttribute('open', 'open');
    }
  }

  /* ===============================
     Header bubble
  =============================== */

  function updateHeaderCartBubble(itemCount) {
    const wrapper = document.querySelector('#cart-icon-bubble');
    if (!wrapper) return;

    if (!itemCount || itemCount <= 0) {
      wrapper.innerHTML = '';
      return;
    }

    let bubble = wrapper.querySelector('.cart-count-bubble');
    if (!bubble) {
      wrapper.innerHTML = `
        <div class="cart-count-bubble">
          <span aria-hidden="true">${itemCount}</span>
          <span class="visually-hidden">${itemCount} items</span>
        </div>
      `;
      return;
    }

    const numberEl =
      bubble.querySelector('span[aria-hidden="true"]') ||
      bubble.querySelector('span');

    if (numberEl) numberEl.textContent = String(itemCount);

    const srEl = bubble.querySelector('.visually-hidden');
    if (srEl) srEl.textContent = `${itemCount} items`;
  }

  /* ===============================
     Add to cart Dawn style
  =============================== */

  async function addToCartDawn(variantId, qty = 1) {
    const params = new URLSearchParams();
    params.set('sections', 'cart-drawer');
    params.set('sections_url', window.location.pathname);

    const res = await fetch(`/cart/add.js?${params.toString()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty }] })
    });

    let data = {};
    try {
      data = await res.json();
    } catch {}

    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || 'No se pudo añadir');
      err.status = data.status || res.status;
      err.data = data;
      throw err;
    }

    return data;
  }

  function replaceCartDrawerFromSectionHTML(cartDrawerHTML) {
    if (!cartDrawerHTML) return;

    const doc = new DOMParser().parseFromString(cartDrawerHTML, 'text/html');

    const newCartDrawer = doc.querySelector('#CartDrawer');
    const currentCartDrawer = document.querySelector('#CartDrawer');

    if (newCartDrawer && currentCartDrawer) {
      currentCartDrawer.replaceWith(newCartDrawer);
      return;
    }

    const fresh = doc.querySelector('cart-drawer');
    const current = document.querySelector('cart-drawer');
    if (fresh && current) current.replaceWith(fresh);
  }

  /* ===============================
     Init Lookbook
  =============================== */

  function initLookbook(wrap) {
    if (!wrap || !wrap.matches('[data-lb-wrap]')) return;

    const section = wrap.closest('[data-lookbook-section]');
    if (!section) return;

    const modal = section.querySelector('[data-lb-modal]');
    const content = section.querySelector('[data-lb-content]');
    const closeEls = section.querySelectorAll('[data-lb-close]');
    if (!modal || !content) return;

    const openModal = () => {
      modal.hidden = false;
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
      modal.classList.remove('is-open');
      modal.hidden = true;
      content.innerHTML = '';
      document.body.style.overflow = '';
    };

    if (!section.dataset.lbModalBound) {
      section.dataset.lbModalBound = '1';

      closeEls.forEach(el => el.addEventListener('click', closeModal));

      modal.addEventListener('click', (e) => {
        if (e.target.closest('.lb-modal__dialog')) return;
        closeModal();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.hidden) closeModal();
      });
    }

    /* ===============================
       Render producto en modal
    =============================== */

    async function renderProduct(product, handle) {
      await enrichVariantsIfNeeded(product, handle);

      const first = product.variants.find(v => v.available) || product.variants[0];
      const img = product.images?.[0];

      const tallaIndex = Array.isArray(product.options)
        ? product.options.findIndex(o => String(o).toLowerCase().includes('talla'))
        : -1;

      const optionHTML = product.variants.map(v => {
        const size = (tallaIndex >= 0 && v.options && v.options[tallaIndex])
          ? v.options[tallaIndex]
          : (v.options ? v.options[v.options.length - 1] : v.title);

        const baseLabel = escapeHtml(size);

        return `<option value="${v.id}" data-base-label="${baseLabel}" ${!v.available ? 'disabled' : ''} ${v.id === first.id ? 'selected' : ''}>
          ${baseLabel}${!v.available ? ' — Agotado' : ''}
        </option>`;
      }).join('');

      const compare =
        product.compare_at_price && product.compare_at_price > product.price
          ? product.compare_at_price
          : null;

      content.innerHTML = `
        <div class="lb-card">
          <div class="lb-card__media">
            ${img ? `<img src="${img}" alt="">` : ``}
          </div>

          <div class="lb-card__body">
            <h3 class="lb-card__title">${product.title}</h3>

            <div class="lb-card__prices">
              <span class="lb-card__price">${money(first.price)}</span>
              ${compare ? `<span class="lb-card__compare">${money(compare)}</span>` : ``}
            </div>

            <div class="lb-card__controls">
              <div class="lb-card__field">
                <label class="lb-card__label">Talla</label>
                <select data-variant class="lb-card__select">
                  ${optionHTML}
                </select>
              </div>

              <button data-atc class="button button--full-width" ${first.available ? '' : 'disabled'}>
                ${first.available ? 'Añadir a la bolsa' : 'Agotado'}
              </button>

              <a href="${product.url}" class="lb-card__link link underlined-link">
                Ver producto
              </a>

              <p data-msg class="lb-card__msg"></p>
            </div>
          </div>
        </div>
      `;

      const btn = content.querySelector('[data-atc]');
      const sel = content.querySelector('[data-variant]');
      const msg = content.querySelector('[data-msg]');

      const setButtonForVariant = () => {
        const v = product.variants.find(x => String(x.id) === String(sel.value));
        const opt = sel.options[sel.selectedIndex];
        if (!v || !v.available || (opt && opt.disabled)) {
          btn.disabled = true;
          btn.textContent = 'Agotado';
        } else {
          btn.disabled = false;
          btn.textContent = 'Añadir a la bolsa';
        }
      };

      sel.addEventListener('change', () => {
        msg.textContent = '';
        setButtonForVariant();
        refreshVariantAvailability(product, sel).then(() => setButtonForVariant());
      });

      setButtonForVariant();
      refreshVariantAvailability(product, sel).then(() => setButtonForVariant());

      btn.addEventListener('click', async () => {
        msg.textContent = '';
        btn.disabled = true;
        const original = btn.textContent;
        btn.textContent = 'Añadiendo…';

        const variantId = Number(sel.value);

        try {
          const data = await addToCartDawn(variantId, 1);

          const cart = await fetchCart();
          updateHeaderCartBubble(cart.item_count);

          try {
            const qtyMap = cartQtyByVariant(cart);
            applyAvailabilityToSelect(product, sel, qtyMap);
          } catch {}

          replaceCartDrawerFromSectionHTML(data.sections && data.sections['cart-drawer']);
          openDawnCartDrawer();

          btn.textContent = '¡Añadido a la bolsa!';
          setTimeout(() => {
            msg.textContent = '';
            setButtonForVariant();
          }, 700);

        } catch (err) {
          console.error(err);

          const message = err?.data?.description || err?.message || 'No se pudo añadir.';
          msg.textContent = message;

          if (Number(err?.status) === 422) {
            btn.textContent = 'Agotado';
            btn.disabled = true;

            try {
              const cart = await fetchCart();
              updateHeaderCartBubble(cart.item_count);
            } catch {}

            return;
          }

          btn.textContent = original;
          btn.disabled = false;
        }
      });
    }

    /* ===============================
       Hotspots
    =============================== */

    function createDot(hs) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lb__dot';

      btn.style.setProperty('--x-desktop', `${hs.x_desktop ?? 50}%`);
      btn.style.setProperty('--y-desktop', `${hs.y_desktop ?? 50}%`);
      btn.style.setProperty('--x-mobile', `${hs.x_mobile ?? hs.x_desktop ?? 50}%`);
      btn.style.setProperty('--y-mobile', `${hs.y_mobile ?? hs.y_desktop ?? 50}%`);

      btn.innerHTML = `<span class="lb__dot-inner"></span>`;

      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (!hs.handle) return;

        openModal();
        content.innerHTML = `<div class="lb-loading">Cargando…</div>`;

        try {
          const product = await fetchProduct(hs.handle);
          await renderProduct(product, hs.handle);
        } catch (err) {
          console.error(err);
          content.innerHTML = `<div class="lb-loading">No se pudo cargar el producto.</div>`;
        }
      });

      return btn;
    }

    /* ===============================
       Parse hotspots JSON
    =============================== */

    let hotspots = [];
    try {
      const jsonEl = wrap.querySelector('[data-hotspots-json]');
      if (jsonEl) {
        const raw = (jsonEl.textContent || '').trim();
        if (!raw) {
          hotspots = [];
        } else if (raw.startsWith('[')) {
          hotspots = JSON.parse(raw);
        } else if (raw.startsWith('{')) {
          hotspots = [JSON.parse(raw)];
        } else if (raw.startsWith(',')) {
          hotspots = JSON.parse('[' + raw.replace(/^,+/, '') + ']');
        } else {
          hotspots = JSON.parse('[' + raw + ']');
        }
      }
    } catch (e) {
      console.warn('Hotspots JSON inválido', e);
      hotspots = [];
    }

    if (Array.isArray(hotspots)) {
      hotspots.forEach(hs => wrap.appendChild(createDot(hs)));
    }

        /* ===============================
       Editor mode
    =============================== */

    const editorEnabled =
      wrap.getAttribute('data-editor-enabled') === 'true' &&
      window.Shopify &&
      Shopify.designMode === true;

    if (editorEnabled) {
      const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

      function showToast(text) {
        let el = wrap.querySelector('.lb-toast');
        if (!el) {
          el = document.createElement('div');
          el.className = 'lb-toast';
          wrap.appendChild(el);
        }

        el.textContent = text;
        el.classList.add('is-show');

        clearTimeout(showToast._timer);
        showToast._timer = setTimeout(() => {
          el.classList.remove('is-show');
        }, 1800);
      }

      async function copyText(text) {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return true;
        }
        return false;
      }

      wrap.addEventListener('click', async (e) => {
        if (e.target.closest('.lb__dot')) return;

        const img = wrap.querySelector('.lb__img');
        const target = img || wrap;
        const rect = target.getBoundingClientRect();

        if (!rect.width || !rect.height) return;

        const x = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
        const y = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);

        const xFixed = x.toFixed(1);
        const yFixed = y.toFixed(1);

        const snippet = `,{
  "x_desktop": ${xFixed},
  "y_desktop": ${yFixed},
  "x_mobile": ${xFixed},
  "y_mobile": ${yFixed},
  "handle": "PEGA_HANDLE"
}`;

        try {
          const copied = await copyText(snippet);

          if (copied) {
            showToast(`Copiado: D ${xFixed}%, ${yFixed}% / M ${xFixed}%, ${yFixed}%`);
          } else {
            window.prompt('Copia este hotspot y pégalo en tu JSON:', snippet);
          }
        } catch (err) {
          window.prompt('Copia este hotspot y pégalo en tu JSON:', snippet);
        }
      });
    }
  }

  /* ===============================
     Init
  =============================== */

  function initAllLookbooks() {
    document.querySelectorAll('[data-lookbook][data-lb-wrap]').forEach(initLookbook);
  }

  document.addEventListener('DOMContentLoaded', initAllLookbooks);
  document.addEventListener('shopify:section:load', initAllLookbooks);

})();